<template>
  <ModalDialog
    ref="dialog"
    title="Offcut Details"
    icon="fa-layer-group"
    :showFooter="false"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loadingData" :loadingError="loadingError" />
    <FormGroup v-bind:form="form" :labelColumnSize="3" v-show="!loadingData && !loadingError && glassLevel">
      <TabbedBox>
         <template v-slot:tablist>
          <Tab :first="true" tabid="tab-details">
            <i class="far fa-info-circle"></i> Details
          </Tab>
          <Tab ref="tabIssue" tabid="tab-issue" v-if="isOffcutAvailable">
            <i class="far fa-exclamation-triangle"></i> Issue
          </Tab>
          <Tab tabid="tab-revert" v-if="isOffcutUsed">
            <i class="far fa-sync"></i> Revert
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-details" v-if="glassLevel">
            <div class="float-right">
              <ActionDropMenu>
                <DropdownMenuItem icon="fa-print" @click="reprintLabel" v-if="isOffcutAvailable">Reprint Label</DropdownMenuItem>
              </ActionDropMenu>
            </div>

            <FormRow label="Offcut Details">
              <FormStaticLabel>
                {{glassLevel.barcode}}<br />
                {{glassLevel.stock.code}} - {{glassLevel.stock.name}}<br />
                {{glassLevel.height}} x {{glassLevel.width}}
              </FormStaticLabel>
            </FormRow>

            <div v-if="isOffcutAvailable">
              <FormHorizontalRule />
              <ContentTitle>Offcut Location Details</ContentTitle>
              <ContentSubTitle>Location of the offcut piece</ContentSubTitle>

              <FormRow label="Inventory Bin">
                <FormInventoryBinSelect v-model="glassLevel.inventoryBinId" placeholder="Bin" field="inventoryBinId" />
              </FormRow>
              <FormRow label="Comments">
                <FormInputTextArea v-model="glassLevel.offcutComments" placeholder="Comments" />
              </FormRow>

              <FormHorizontalRule />
              <div class="text-right">
                <FormSubmit label="Update Offcut" buttonSizeClass="btn-lg" :submitting="updateSubmitting" @cancelled="cancelUpdateSubmit" @submitted="submitUpdate"></FormSubmit>
              </div>
            </div>  
          </TabPage>

          <TabPage tabid="tab-issue" v-if="glassLevel">
            <ContentTitle>Offcut Issue</ContentTitle>
            <ContentSubTitle>Flag the offcut has having an issue &amp; remove it from inventory</ContentSubTitle>

            <FormRow label="Reporting User">
              <FormInputText v-model="glassLevel.offcutIssueReportingUser" placeholder="Reporting User" field="offcutIssueReportingUser" />
            </FormRow>
            <FormRow label="Notes">
              <FormInputTextArea v-model="glassLevel.offcutIssueNotes" placeholder="Notes" field="offcutIssueNotes" />
            </FormRow>
            <FormRow label="Issue Labels">
              <FormLabelTag popupName="Issue Labels" v-model="issueLabelList" labelType="glassLevelIssue" field="labelList" />
            </FormRow>    

            <FormHorizontalRule />
            <div class="text-right">
              <FormSubmit label="Remove Offcut" buttonSubmitClass="btn-danger" buttonSizeClass="btn-lg" :submitting="issueSubmitting" @cancelled="cancelIssueSubmit" @submitted="submitIssue"></FormSubmit>
            </div>
          </TabPage>

          <TabPage tabid="tab-revert" v-if="glassLevel">
            <ContentTitle>Revert Offcut</ContentTitle>
            <ContentSubTitle>Revert an offcut previously flagged with an issue &amp; move it back into inventory</ContentSubTitle>

            <FormRow label="Reason">
              <FormInputText v-model="revertNotes" placeholder="Notes" field="revertNotes" />
            </FormRow>

            <FormHorizontalRule />
            <div class="text-right">
              <FormSubmit label="Revert Offcut" buttonSizeClass="btn-lg" :submitting="revertSubmitting" @cancelled="cancelRevertSubmit" @submitted="submitRevert"></FormSubmit>
            </div>
          </TabPage>
        </template>
      </TabbedBox>      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'

import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import FormInventoryBinSelect from '@/modules/inventory/components/formInventoryBinSelect'
import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'

import EventBus from '@/utils/eventBus'

export default {
  name: 'GlassOffcutStationDialog',
  mixins: [
    modalDialog,
    form,
    ProductionStationMixin
  ],
  components: {
    ModalDialog,
    FormInventoryBinSelect
  },
  props: {
    id: Number,
    showOffcutIssueTab: Boolean,
    productionStageId: Number,
    productionStationId: Number
  },
  data() {
    return {
      loadingData: false,
      loadingError: '',
      form: {},
      glassLevel: null,
      issueLabelList: [],
      updateSubmitting: false,
      issueSubmitting: false,
      revertSubmitting: false,
      revertNotes: ''
    }
  },
  created() {
  },
  mounted() {
  },
  computed: {
    isOffcutAvailable() {
      if(!this.glassLevel)
        return false
      return this.glassLevel.isOffcut && this.glassLevel.isOffcutAvailable && this.glassLevel.isActive && this.glassLevel.qtyOnHand > 0 && !this.glassLevel.isOffcutIssue
    },
    isOffcutUsed() {
      if(!this.glassLevel)
        return false
      return this.glassLevel.isOffcut && !this.glassLevel.isActive && this.glassLevel.isOffcutIssue
    }    
  },
  methods: {
    show() {
      this.form = {}

      this.submitting = false

      // Load the offcut details
      this.requestGlassData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
    },
    requestGlassData() {
      this.loadingData = true

      this.$apiService.glassProductionStationService.getGlassLevel(this.productionStationId, this.id)
      .then(response => {
        this.glassLevel = response.data

        // Show issue tab if requested
        this.$nextTick(() => {
          if(this.showOffcutIssueTab && this.$refs.tabIssue) {
            this.$refs.tabIssue.show();
          }
        });
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loadingData = false
      })
    },
    reprintLabel() {
      this.$apiService.glassProductionStationService.generatePrintOffcutLabelURL(this.productionStationId, this.id, { download: true, label: true })
      .then(response => {
        setTimeout(() => {
          this.displayPopupAlert('success', `Label Printed`, `Label printed for offcut`)
        }, 50)

        // Set to download
        window.location.href = response.data.url
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Print Error')
      })
    },
    cancelUpdateSubmit() {
      this.modalClosed(null)
      this.hide()
    },
    submitUpdate() {
      this.updateSubmitting = true
      this.form = this.clearFormResponse()

      let offcutUpdateData = {
        inventoryBinId: this.glassLevel.inventoryBinId,
        offcutComments: this.glassLevel.offcutComments
      }
      
      this.$apiService.glassProductionStationService.updateGlassLevel(this.productionStationId, this.id, offcutUpdateData)
      .then(response => {
        this.successToast('Offcut Updated')
        this.modalClosed(null)
        this.hide()
      })
      .catch(err => {
        this.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.updateSubmitting = false
      })
    },
    cancelIssueSubmit() {
      this.hide()
    },
    submitIssue() {
      this.issueSubmitting = true
      this.form = this.clearFormResponse()

      let offcutIssueData = {
        offcutIssueReportingUser: this.glassLevel.offcutIssueReportingUser,
        offcutIssueNotes: this.glassLevel.offcutIssueNotes,
        labelList: this.issueLabelList.map(label => label.id)
      }
      
      this.$apiService.glassProductionStationService.createGlassLevelIssue(this.productionStationId, this.id, offcutIssueData)
      .then(response => {
        this.successToast('Offcut Issue Submitted')
        this.modalClosed(null)
        this.hide()

        EventBus.emitEvent(EventBus.EventTypes.offcutIssueCreated, { glassLevelId: this.id })
      })
      .catch(err => {
        this.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.issueSubmitting = false
      })
    },
    cancelRevertSubmit() {
      this.hide()
    },
    submitRevert() {
      this.revertSubmitting = true
      this.form = this.clearFormResponse()

      let revertData = {
        revertNotes: this.revertNotes
      }
      
      this.$apiService.glassProductionStationService.revertGlassLevelIssue(this.productionStationId, this.id, revertData)
      .then(response => {
        this.successToast('Offcut Issue Reverted')
        this.modalClosed(null)
        this.hide()
      })
      .catch(err => {
        this.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.revertSubmitting = false
      })
    }


  }
}
</script>